import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer, Subject } from 'rxjs';
import { debounceTime, buffer } from 'rxjs/operators';

export interface NotificationMessage {
  id: string;
  type: 'order' | 'document' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  orderIds?: string[];
  showCloseButton?: boolean;
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications$ = new BehaviorSubject<NotificationMessage[]>([]);
  private newOrderSubject = new Subject<any>();
  private soundEnabled = true;
  private audio: HTMLAudioElement | null = null;

  constructor() {
    this.initializeAudio();
    this.setupOrderBuffering();
  }

  /**
   * Initialize audio for notification sounds
   */
  private initializeAudio(): void {
    try {
      // Create audio element for notification sound
      this.audio = new Audio();
      this.audio.preload = 'auto';
      // We'll create the audio file later
      this.audio.src = '/assets/sounds/notification.mp3';
      this.audio.volume = 0.7;
    } catch (error) {
      console.warn('Audio initialization failed:', error);
      this.audio = null;
    }
  }

  /**
   * Setup order buffering to handle multiple orders in short periods
   */
  private setupOrderBuffering(): void {
    this.newOrderSubject
      .pipe(
        buffer(this.newOrderSubject.pipe(debounceTime(2000))), // Wait 2 seconds after last order
        debounceTime(100) // Small delay to ensure buffer is complete
      )
      .subscribe((orders: any[]) => {
        if (orders.length > 0) {
          this.createOrderNotification(orders);
        }
      });
  }

  /**
   * Get current notifications as observable
   */
  getNotifications(): Observable<NotificationMessage[]> {
    return this.notifications$.asObservable();
  }

  /**
   * Add new order notification (buffered)
   */
  addOrderNotification(order: any): void {
    this.newOrderSubject.next(order);
  }

  /**
   * Create buffered order notification
   */
  private createOrderNotification(orders: any[]): void {
    const orderCount = orders.length;
    const orderIds = orders.map(order => order.uniqueId || order.id);
    
    let title: string;
    let message: string;

    if (orderCount === 1) {
      title = 'New Shopee Order Received';
      message = `Order #${orders[0].shopeeOrderNo || orderIds[0]} has been received`;
    } else {
      title = 'New Orders Received';
      message = `${orderCount} new Shopee orders have been received`;
    }

    const notification: NotificationMessage = {
      id: this.generateId(),
      type: 'order',
      title,
      message,
      timestamp: new Date(),
      orderIds,
      showCloseButton: true,
      duration: 4000
    };

    this.addNotification(notification);
    this.showBrowserNotification(notification);
    this.playNotificationSound();
  }

  /**
   * Add a general notification
   */
  addNotification(notification: NotificationMessage): void {
    const current = this.notifications$.value;
    this.notifications$.next([notification, ...current]);

    // Auto-remove after duration if specified
    if (notification.duration && notification.duration > 0) {
      timer(notification.duration).subscribe(() => {
        this.removeNotification(notification.id);
      });
    }
  }

  /**
   * Remove notification by ID
   */
  removeNotification(id: string): void {
    const current = this.notifications$.value;
    const updated = current.filter(n => n.id !== id);
    this.notifications$.next(updated);
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications(): void {
    this.notifications$.next([]);
  }

  /**
   * Show browser notification (desktop notification)
   */
  private showBrowserNotification(notification: NotificationMessage): void {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return;
    }

    if (Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.title, {
        body: notification.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: `order-${notification.id}`,
        requireInteraction: false,
        silent: false
      });

      // Handle notification click
      browserNotification.onclick = () => {
        window.focus();
        this.handleNotificationClick(notification);
        browserNotification.close();
      };

      // Auto-close browser notification
      setTimeout(() => {
        browserNotification.close();
      }, notification.duration || 4000);

    } else if (Notification.permission !== 'denied') {
      // Request permission
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.showBrowserNotification(notification);
        }
      });
    }
  }

  /**
   * Handle notification click event
   */
  private handleNotificationClick(notification: NotificationMessage): void {
    if (notification.type === 'order' && notification.orderIds) {
      // Emit event for highlighting new orders
      this.highlightNewOrders(notification.orderIds);
    }
  }

  /**
   * Highlight new orders (will be handled by order components)
   */
  private highlightNewOrders(orderIds: string[]): void {
    // Store in session storage for order list component to pick up
    sessionStorage.setItem('highlightOrders', JSON.stringify({
      orderIds,
      timestamp: Date.now(),
      duration: 30000 // Highlight for 30 seconds
    }));

    // Navigate to orders page if not already there
    if (!window.location.pathname.includes('/orders')) {
      window.location.href = '/dashboard/orders';
    }
  }

  /**
   * Play notification sound
   */
  private playNotificationSound(): void {
    if (!this.soundEnabled || !this.audio) {
      return;
    }

    try {
      this.audio.currentTime = 0;
      const playPromise = this.audio.play();
      
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Audio play failed:', error);
        });
      }
    } catch (error) {
      console.warn('Audio play error:', error);
    }
  }

  /**
   * Toggle sound on/off
   */
  toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    localStorage.setItem('notificationSoundEnabled', this.soundEnabled.toString());
  }

  /**
   * Check if sound is enabled
   */
  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  /**
   * Request browser notification permission
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Generate unique ID for notifications
   */
  private generateId(): string {
    return `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize service after user login
   */
  initialize(): void {
    // Load sound preference
    const soundPref = localStorage.getItem('notificationSoundEnabled');
    if (soundPref !== null) {
      this.soundEnabled = soundPref === 'true';
    }

    // Request notification permission
    this.requestNotificationPermission();
  }
}
